import { useRef, useEffect, memo } from 'react';
import MessageBubble from './MessageBubble';

/**
 * MessageList - Scrollable message container with auto-scroll
 * Apple Messages-inspired design with light/dark mode support
 */

function MessageList({ messages, currentUserId, loading = false }) {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Group consecutive messages from same sender
  const groupedMessages = messages.reduce((groups, message, index) => {
    const prevMessage = messages[index - 1];
    const showSender = !prevMessage ||
      prevMessage.senderId !== message.senderId ||
      prevMessage.type === 'system' ||
      message.type === 'system';

    groups.push({ ...message, showSender });
    return groups;
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-[#86868b] text-sm">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-white dark:bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
            <svg className="w-10 h-10 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-[#1d1d1f] dark:text-white/70 font-medium text-sm mb-2">No messages yet</h3>
          <p className="text-[#86868b] text-xs leading-relaxed">
            This is the beginning of the conversation. Send a message to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
    >
      {groupedMessages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          isOwn={message.senderId === currentUserId}
          showSender={message.showSender}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default memo(MessageList);
